import React, { useEffect, useState } from 'react';
import { useAuth } from "../../Context/AuthContext";
import Layout from '../../components/Layout';
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Container,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../constants';
import {
  BannerHeading,
  BannerContainer,
  CourseCard,
} from "../../components/services/ServicesElements";
import axios from 'axios';
import ConfirmDeleteModal from '../../components/DeleteModal';

const initialJobState = () => ({
  title: '',
  description: '',
  link: '',
  company: '',
  type: '',
  requirements: '',
});

const Job = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newJob, setNewJob] = useState(initialJobState());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const token = localStorage.getItem('token');

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/job`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setErrorMessage('Failed to fetch jobs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleChange = (e) => {
    setNewJob({ ...newJob, [e.target.name]: e.target.value });
  };

  const handleEdit = (job) => {
    setIsEditMode(true);
    setCurrentJobId(job._id);
    setNewJob({
      ...job,
      requirements: job.requirements.join('\n'),
    });
    setShowModal(true);
  };

  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/job/${jobToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(jobs.filter((job) => job._id !== jobToDelete._id));
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting job:', error);
      setErrorMessage('Failed to delete job.');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setNewJob(initialJobState());
    setIsEditMode(false);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formattedJob = {
        ...newJob,
        requirements: newJob.requirements
          .split('\n')
          .map((req) => req.trim())
          .filter(Boolean),
      };

      const response = isEditMode
        ? await axios.put(`${BASE_URL}/job/${currentJobId}`, formattedJob, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : await axios.post(`${BASE_URL}/job`, formattedJob, {
            headers: { Authorization: `Bearer ${token}` },
          });

      if ([200, 201].includes(response.status)) {
        setJobs((prevJobs) =>
          isEditMode
            ? prevJobs.map((job) =>
                job._id === currentJobId ? response.data : job
              )
            : [...prevJobs, response.data]
        );
        setSuccessMessage(isEditMode ? 'Job updated successfully!' : 'Job created successfully!');
        setTimeout(() => handleModalClose(), 2000);
      }
    } catch (error) {
      console.error('Error submitting job:', error);
      setErrorMessage('Failed to submit job. Please check your input and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <BannerContainer>
        <div className="my-4">
          <BannerHeading className="text-center mb-4">Job Offers</BannerHeading>

          <div className="d-flex justify-content-between align-items-center mb-3">
   
            <Button
              variant="cyan"
              onClick={() => {
                setIsEditMode(false);
                setCurrentJobId(null);
                setNewJob(initialJobState());
                setShowModal(true);
              }}
            >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                <span className="small">Add Job</span> 
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : jobs.length ? (
            <Row xs={1} sm={2} md={3} className="g-4">
              {jobs.map((job) => (
             <Col key={job._id}>
             <CourseCard className="p-3 shadow-sm h-100 border rounded">
               <div className="d-flex justify-content-end mb-2">
                 <FontAwesomeIcon
                   icon={faEdit}
                   className="text-primary me-2 cursor-pointer"
                   onClick={() => handleEdit(job)}
                   role="button"
                 />
                 <FontAwesomeIcon
                   icon={faTrashAlt}
                   className="text-danger cursor-pointer"
                   onClick={() => handleDeleteJob(job)}
                   role="button"
                 />
               </div>
           
               {/* Job Title - Centered */}
               <div className="text-center mb-3">
                 <h5 className="fw-bold">{job.title}</h5>
               </div>
           
               {/* Job Details */}
               <p className="text-muted">{job.description}</p>
               <p><strong>Company:</strong> {job.company}</p>
               <p>
                 <strong>Link:</strong>{' '}
                 <a href={job.link} target="_blank" rel="noopener noreferrer">
                   {job.link}
                 </a>
               </p>
               <p><strong>Type:</strong> {job.type}</p>
               <p>
                 <strong>Requirements:</strong>
                 <ul className="ps-3 mb-0">
                   {job.requirements.map((req, idx) => (
                     <li key={idx}>{req}</li>
                   ))}
                 </ul>
               </p>
             </CourseCard>
           </Col>
           
              ))}
            </Row>
          ) : (
           <div className="card mt-3">
            <div className="card-body">
              <p className="card-text">No jobs available. Please add some.</p>
            </div>
          </div>
          )}
        </div>
      </BannerContainer>

      <ConfirmDeleteModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        itemName="this job"
      />

<Modal show={showModal} onHide={handleModalClose}>
  <Modal.Header closeButton>
    <Modal.Title>{isEditMode ? 'Edit Job' : 'Add New Job'}</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form onSubmit={handleSubmitJob}>
      {[
        ['title', 'Job Title', 'Enter job title'],
        ['description', 'Description', 'Enter job description'],
        ['link', 'Job Link', 'Enter job application link'],
        ['company', 'Company', 'Enter company name'],
      ].map(([field, label, placeholder]) => (
        <Form.Group key={field} className="mb-3">
          <Form.Label>
            {label}
            {!isEditMode && ' *'}
          </Form.Label>
          <Form.Control
            type="text"
            as={field === 'description' ? 'textarea' : 'input'}
            rows={field === 'description' ? 3 : undefined}
            name={field}
            value={newJob[field]}
            onChange={handleChange}
            placeholder={placeholder}
            required={!isEditMode}
          />
        </Form.Group>
      ))}

      <Form.Group className="mb-3">
        <Form.Label>
          Type
          {!isEditMode && ' *'}
        </Form.Label>
        <Form.Select
          name="type"
          value={newJob.type}
          onChange={handleChange}
          required={!isEditMode}
        >
          <option value="">Select Job Type</option>
          <option value="Full-Time">Full-Time</option>
          <option value="Part-Time">Part-Time</option>
          <option value="Contract">Contract</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          Requirements
          {!isEditMode && ' *'}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          name="requirements"
          value={newJob.requirements}
          onChange={handleChange}
          placeholder="Enter each requirement on a new line"
          required={!isEditMode}
        />
      </Form.Group>

      <div className="text-end">
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" /> Saving...
            </>
          ) : isEditMode ? 'Update' : 'Save'}
        </Button>
      </div>
    </Form>
    <div className="mt-2">
    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
    {successMessage && <Alert variant="success">{successMessage}</Alert>}
    </div>
  </Modal.Body>
</Modal>

    </Layout>
  );
};

export default Job;
